"use client";
import { ROUTES } from "@/@core/constants/routes";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/CustomLink";
import { usePathname, useSearchParams } from "next/navigation";
import { lazy, useEffect, useMemo, useState } from "react";
import MagnifyingGlassIcon from "@heroicons/react/24/outline/MagnifyingGlassIcon";
import TicketIcon from "@heroicons/react/24/outline/TicketIcon";
import ShareIcon from "@heroicons/react/24/solid/ShareIcon";
import { useSearchContext } from "@/lib/hooks/searchContext/searchStore";
import IsMobileBlock from "@/components/IsMobileBlock";
import { useSite } from "@/lib/hooks/site";
import { useVenueListPage } from "@/hooks/listPageContext";
import { useTranslation } from "@/hooks/translationContext";
import { useVenueDetail } from "@/hooks/venueContext";
import { usePlausible } from "next-plausible";
import {
  CLICK_QUOTE_EVENT_SECTIONS,
  PLAUSIBLE_GOAL_KEYS,
} from "@/@core/constants/plausible";
import { useContactWithVenuesLink } from "@/hooks/useContactWithVenuesLink";
import { useIsAskQuestionEnabled } from "@/hooks/useIsAskQuestionEnabled";
import PercentBadgeIcon from "@heroicons/react/24/outline/PercentBadgeIcon";
import CalendarIcon from "@heroicons/react/24/outline/CalendarIcon";
import { ListBulletIcon, MapIcon } from "@heroicons/react/24/solid";
import { useViewMode } from "@/context/ViewModeContext";
import { isPhysiotherapistVenue } from "@/lib/venue-business-type";

const VenueShareModal = lazy(() => import("../views/modals/venue/ShareModal"));

const VenueDetailContactModal = lazy(
  () => import("@/components/views/venue-details/new-details/ContactModal"),
);

const SharePageModal = lazy(
  () => import("@/components/views/modals/SharePageModal"),
);
export default function BottomBar() {
  const { t } = useTranslation();
  const [isContactModal, setIsContactModal] = useState(false);
  const [isVenueShareModal, setIsVenueShareModal] = useState(false);
  const [isShareModal, setIsShareModal] = useState(false);
  const [visible, setVisible] = useState(false);

  const pathName = usePathname();
  const searchParams = useSearchParams();
  const { setIsOpen } = useSearchContext();
  const pathnames = ["/"];
  const { venue } = useVenueDetail();
  const { isVenueListPage } = useVenueListPage();
  const getQuoteHref = useContactWithVenuesLink();
  const isAskQuestionEnabled = useIsAskQuestionEnabled();

  const site = useSite();
  const plausible = usePlausible();

  const { toggleViewMode, viewMode } = useViewMode();
  const isTrainerPage = `/${site?.setting?.routes?.trainer}` == pathName;
  const isActiveMapPaths = ["/blog", "/kampanyalar", "/etkinlikler", "/sirket-indirimleri"]

  // The venue in context outlives its page, so it only counts on a venue page.
  // Physiotherapist venue pages link to no campaigns or discounts (SAB-362).
  const isPhysiotherapistVenuePage =
    pathName.startsWith(`/${site?.setting?.routes?.venueDetail}/`) &&
    isPhysiotherapistVenue(venue);

  const handleScroll = () => {
    const scrollPosition = window.scrollY;
    const venueDetailPath = `/${site.setting?.routes?.venueDetail}/`;

    const offset = pathName.startsWith(venueDetailPath) ? 40 : 280;

    if (scrollPosition >= offset) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  };

  useEffect(() => {
    const venueDetailPath = `/${site.setting?.routes?.venueDetail}/`;
    const isVenueDetailPage = pathName.startsWith(venueDetailPath);

    if (isVenueDetailPage || pathnames.includes(pathName) || isVenueListPage) {
      handleScroll();
      window.addEventListener("scroll", handleScroll, { passive: true });
    } else {
      setVisible(true);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [pathName, isVenueListPage]);

  const pageButtons = [
    {
      title: t("text-menu-content-campaign", {
        defaultValue: "Kampanyalar",
      }),
      isHidden:
        site?.setting?.disabledPages?.campaign === 1 ||
        isPhysiotherapistVenuePage,
      enabled: site?.setting?.pageButtons?.navigation?.includes("campaign"),
      icon: <TicketIcon className="w-[24px] h-[24px]" />,
      path: ROUTES.CAMPAIGNS,
      disabled: false,
    },
    {
      title: t("text-menu-content-event", {
        defaultValue: "Etkinlikler",
      }),
      isHidden: site?.setting?.disabledPages?.event === 1,
      enabled: site?.setting?.pageButtons?.navigation?.includes("event"),
      icon: <CalendarIcon className="w-[24px] h-[24px]" />,
      path: ROUTES.EVENTS,
      disabled: false,
    },
    {
      title: "İndirimler",
      isHidden:
        site?.setting?.disabledPages?.company === 1 ||
        isPhysiotherapistVenuePage,
      enabled:
        site?.setting?.pageButtons?.navigation?.includes("companyDiscounts"),
      icon: <PercentBadgeIcon className="w-[24px] h-[24px]" />,
      path: ROUTES.ALL_COMPANIES,
      disabled: false,
    },
  ];

  const bottomButtons = [
    {
      title: "Arama",
      icon: <MagnifyingGlassIcon className="w-[24px] h-[24px]" />,
      onClick: () => setIsOpen(true),
      disabled: false,
    },
    {
      title: "Paylaş",
      icon: <ShareIcon className="w-[24px] h-[24px]" />,
      onClick: () => {
        if (pathName.startsWith(`/${site.setting?.routes?.venueDetail}/`)) {
          setIsVenueShareModal(true);
        } else {
          setIsShareModal(true);
        }
      },
      disabled: false,
    },
  ];

  const filteredBottomButtons = useMemo(() => {
    if (!Array.isArray(site?.setting?.pageButtons?.navigation)) {
      return pageButtons[0] && !pageButtons[0].isHidden ? [pageButtons[0]] : [];
    }

    return pageButtons.filter(({ isHidden, enabled }) => !isHidden && (enabled ?? true));
  }, [pageButtons]);


  const getQuoteTitle = () => {
    return t("quote-button-title");
  };

  const sendPlausibleEvent = () => {
    plausible(PLAUSIBLE_GOAL_KEYS.CLICK_QUOTE_EVENT, {
      props: {
        section: CLICK_QUOTE_EVENT_SECTIONS.BOTTOM_BAR,
      },
    });
  };

  return (
    <>
      {visible && (
        <IsMobileBlock>
          <div className="fixed z-[999] h-[62px] px-2 bottom-0 left-0 right-0 flex flex-row md:hidden items-center bg-white shadow-bottom-bar duration-200">
            <div className="flex gap-1 items-start justify-center">
              {bottomButtons.map((button, index) => {
                return (
                  <Button
                    key={index}
                    disabled={button.disabled}
                    className={`rounded-xl ${button.disabled ? "!cursor-not-allowed" : ""}`}
                    variant="nav"
                    onClick={button.onClick}
                    size="sm"
                  >
                    <div className="flex flex-col gap-1 text-[10px] items-center">
                      {button.icon} {button.title}
                    </div>
                  </Button>
                );
              })}

              {filteredBottomButtons.map((button, index) => {
                return (
                  <Link
                    title={button.title}
                    key={index}
                    href={button.path + "?" + searchParams.toString()}
                    className={`${button.disabled ? "!pointer-events-none !cursor-not-allowed" : ""}`}
                  >
                    <Button className={`rounded-xl`} variant="nav" size="sm">
                      <div className="flex flex-col gap-1 text-[10px] items-center">
                        {button.icon} {button.title}
                      </div>
                    </Button>
                  </Link>
                );
              })}

              {(isVenueListPage || isTrainerPage || pathName.startsWith("/blog/") || isActiveMapPaths.includes(pathName)) && (
                <Button onClick={toggleViewMode} className={`rounded-xl`} variant="nav" size="sm">
                  <div className="flex flex-col gap-1 text-[10px] items-center">
                    {viewMode === "list" ? (
                      <>
                        <MapIcon className="w-[24px] h-[24px]" />
                        Harita
                      </>
                    ) : (
                      <>
                        <ListBulletIcon className="w-[24px] h-[24px]" />
                        Liste
                      </>
                    )}
                  </div>
                </Button>
              )}
            </div>

            <div className="flex justify-end w-full z-10">
              {venue?.id &&
                pathName.startsWith(
                  `/${site.setting?.routes?.venueDetail}` + "/",
                ) ? (
                <Button
                  onClick={() => {
                    setIsContactModal(true);
                  }}
                  variant="gradient"
                  size="lg"
                  className="max-md:h-[42px] !px-4 !py-3 !text-sm"
                >
                  İletişime Geç
                </Button>
              ) : isAskQuestionEnabled ? (
                <Link
                  onClick={() => sendPlausibleEvent()}
                  title={getQuoteTitle()}
                  aria-label={getQuoteTitle()}
                  href={getQuoteHref}
                >
                  <Button
                    variant="gradient"
                    className="max-md:h-[42px] flex items-center text-sm justify-center gap-2 !px-4 !py-3"
                  >
                    {getQuoteTitle()}
                  </Button>
                </Link>
              ) : null}
            </div>
          </div>
        </IsMobileBlock>
      )}

      {isContactModal && (
        <VenueDetailContactModal
          isOpen={isContactModal}
          onClose={() => setIsContactModal(false)}
        />
      )}

      {isVenueShareModal && (
        <VenueShareModal
          isOpen={isVenueShareModal}
          onClose={() => setIsVenueShareModal(false)}
        />
      )}

      {isShareModal && (
        <SharePageModal
          isOpen={isShareModal}
          onClose={() => setIsShareModal(false)}
        />
      )}
    </>
  );
}
